import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { useCallback } from "react";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { DropDown } from "../../../../../../common/domain/models/dropdown";
import { DefaultFormMultiAutocomplete } from "../../../../../../common/components/default_form_multi_autocomplete";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { defaultFluxoCaixaForm, FluxoCaixaForm, FluxoCaixaSchema } from "../../../schemas/Principal_financeiro/Fluxo_caixa_form";
import { DefaultFormCheckBox } from "../../../../../../common/components/default_form_checkbox";
import { DefaultFormSelectInput } from "../../../../../../common/components/default_form_select_input";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";

type props = {
    open: boolean,
    saving: boolean,
    ddlCentroCusto: CentroCustoDropDown[];
    ddlFornecedor: DropDown[];
    ddlCliente: DropDown[];
    ddlBanco: DropDown[];

    onClose: () => void,
    onSubmit: (form: FluxoCaixaForm) => void,

}

export const FluxoDeCaixaReportModal = ({
    open,
    saving,
    ddlCentroCusto,
    ddlFornecedor,
    ddlCliente,
    ddlBanco,

    onClose,
    onSubmit,

}: props) => {

    const hookForm = useForm<FluxoCaixaForm>({
        mode: "onSubmit",
        reValidateMode: "onSubmit",
        defaultValues: defaultFluxoCaixaForm,
        resolver: zodResolver(FluxoCaixaSchema),
    });

    const handleClearFilters = useCallback(() => {
        hookForm.reset(defaultFluxoCaixaForm);

    }, [hookForm]);

    const handleSubmit = useCallback((form: FluxoCaixaForm) => {
        if (!saving) {
            onSubmit(form);
        }
    }, [saving, onSubmit]);

    const handleClose = useCallback(() => {
        if (!saving) onClose();
    }, [saving, onClose]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            slotProps={{
                paper: {
                    component: 'form',
                    sx: { width: 400 },
                    onSubmit: hookForm.handleSubmit(handleSubmit)
                }
            }}
            sx={{
                "& .MuiDialog-container": {
                    "& .MuiPaper-root": {
                        width: "100%",
                        maxWidth: "60rem",
                    },
                },
            }}>

            <DialogTitle>
                Relatório de Fluxo de Caixa
            </DialogTitle>

            <DialogContent>
                <ModalLoading loading={saving}  >
                    <FormContainer title={"Recomendações"} arrangement="fullRow">
                        <>
                            <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                                <p> <strong>Campos Obrigatórios:</strong> Os campos datas e centro de custo são obrigatórios para geração do relatório. </p>
                                <p> <strong>Dica:</strong> Sempre que possível, faça filtros direcionados! </p>
                            </div>
                        </>
                    </FormContainer>

                    <FormContainer title={"Informações"} arrangement="fullRow">
                        <>
                            <DefaultFormCheckBox
                                name="isEstoque"
                                label="Incluir entrada de estoque na pesquisa"
                                reactHookForm={hookForm}
                            />

                            <DefaultFormMultiAutocomplete
                                name='idsBancoPagamento'
                                label='Bancos de Pagamento'
                                options={ddlBanco}
                                getOptionKey={x => x.id}
                                getOptionLabel={x => x.title}
                                reactHookForm={hookForm}
                            />

                            <DefaultFormMultiAutocomplete
                                name='idsCentroCusto'
                                label='Centro de Custos'
                                options={ddlCentroCusto}
                                required
                                getOptionKey={x => x.id}
                                getOptionLabel={x => x.title}
                                reactHookForm={hookForm}

                            />

                            <div className="grid grid-cols-2 gap-4 mt-3">
                                <DefaultFormSelectInput
                                    name="statusPagamento"
                                    label="Status do Pagamento"
                                    readOnly={false}
                                    reactHookForm={hookForm}
                                    initialOptionId={3}
                                    initialOptionLabel="Todos"
                                    options={[
                                        { id: 0, label: "Pendente" },
                                        { id: 1, label: "Pago" },
                                        { id: 2, label: "Outros" },
                                    ]}
                                />

                                <DefaultFormSelectInput
                                    name="statusRecebimento"
                                    label="Status do Recebimento"
                                    readOnly={false}
                                    reactHookForm={hookForm}
                                    initialOptionId={3}
                                    initialOptionLabel="Todos"
                                    options={[
                                        { id: 0, label: "Pendente" },
                                        { id: 1, label: "Recebido" },
                                        { id: 2, label: "Outros" },
                                    ]}
                                />

                            </div>


                            <DefaultFormAutocomplete
                                name='idFornecedor'
                                label='Fornecedor'
                                options={ddlFornecedor}
                                getOptionKey={x => x.id}
                                getOptionLabel={x => x.title}
                                reactHookForm={hookForm}
                            />

                            <DefaultFormAutocomplete
                                name='idCliente'
                                label='Cliente'
                                options={ddlCliente}
                                getOptionKey={x => x.id}
                                getOptionLabel={x => x.title}
                                reactHookForm={hookForm}
                            />

                            <div className="grid grid-cols-2 gap-4 mb-3">
                                <FormDatePickerInput
                                    name="dataDe"
                                    label="Data De"
                                    required
                                    reactHookForm={hookForm}
                                />

                                <FormDatePickerInput
                                    name="dataAte"
                                    label="Data Até"
                                    required
                                    reactHookForm={hookForm}
                                />
                            </div>

                        </>
                    </FormContainer>

                </ModalLoading>
            </DialogContent>

            <DialogActions>
                <div className="w-full h-full items-center justify-end flex px-4 pb-4 gap-4">

                    <div className="flex flex-row gap-2">

                        <div className="p-2">
                            <PrimaryButton
                                type="button"
                                buttonColor="yellow"
                                onClick={handleClearFilters}
                            >

                                Limpar Filtro
                            </PrimaryButton>
                        </div>

                        <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                            <h1
                                className="text-sm text-black dark:text-white"
                                onClick={handleClose}>
                                Cancelar
                            </h1>
                        </div>

                        <div className="w-[120px]">
                            {saving ? (
                                <PrimaryButton
                                    buttonColor="primary-green">
                                    Salvando...
                                </PrimaryButton>
                            ) : (
                                <PrimaryButton
                                    type="submit"
                                    buttonColor="primary-green">
                                    Gerar
                                </PrimaryButton>
                            )}
                        </div>
                    </div>
                </div>
            </DialogActions>

        </Dialog>
    );
}